/**
 * Converts a duration string (e.g., '1D', '1W', '1M', '3M', '1Y') to approximate days for comparison.
 * TODO: Use the validation service (https://talostrading.atlassian.net/browse/PORT-17960) to check this instead
 * @param duration - Duration string in format like '1D', '1W', '1M', '3M', '1Y'
 * @returns Approximate number of days, or null if format is invalid
 */
export function durationToDays(duration: string): number | null {
  const match = duration.match(/^(\d+)([A-Z])$/);
  if (!match) {
    return null;
  }

  const [, numberStr, unit] = match;
  const number = parseInt(numberStr, 10);

  if (isNaN(number)) {
    return null;
  }

  // This is using the same approximation logic as the backend does around duration behavior. (standard finance rules)
  switch (unit) {
    case 'D':
      return number;
    case 'W':
      return number * 7;
    case 'M':
      return number * 30; // Approximate month as 30 days
    case 'Y':
      return number * 365; // Approximate year as 365 days
    default:
      return null;
  }
}

/**
 * Compares two duration strings to check if the first is less than or equal to the second
 * @param duration1 - First duration string (e.g., '1D', '1W')
 * @param duration2 - Second duration string (e.g., '1M', '3M')
 * @returns true if duration1 <= duration2, false otherwise, or null if either has invalid format
 */
export function isDurationLessThanOrEqual(duration1: string, duration2: string): boolean | null {
  const days1 = durationToDays(duration1);
  const days2 = durationToDays(duration2);

  if (days1 === null || days2 === null) {
    return null;
  }

  return days1 <= days2;
}

/** Max tenor for loan OTC `term` / `initialTerm` (compare via `durationToDays` approximations). */
export const LOAN_OTC_MAX_TERM = '1Y';

/**
 * Whether a duration is at most {@link LOAN_OTC_MAX_TERM}. Empty values pass (use `required` / `when` for presence).
 * Unparseable strings fail (strict).
 */
export function isDurationWithinLoanOtcMax(value: string | null | undefined): boolean {
  if (value == null || value === '') {
    return true;
  }
  const cmp = isDurationLessThanOrEqual(value, LOAN_OTC_MAX_TERM);
  if (cmp === null) {
    return false;
  }
  return cmp;
}
